import { toastNotifications } from '@uag/react-core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { useLoadProject } from 'api/queries/projects/projects';
import { PendingLoadedProject } from 'app/modules/wallforming/components/projects-manager/types/pendingLoadedProject';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { createProjectSignature } from 'app/modules/wallforming/components/projects-manager/utils/projectUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';

interface UseProjectLoadInput {
    activeSessionId: string;
}

/**
 * Manages the full project-load flow:
 * - Calls the load API and immediately clears articles / POI article assignments.
 * - Stores a `pendingLoadedProject` and applies it (via `handlePlaceArticleGroupsAsync`)
 *   once the SignalR session ID and POI data are in sync — tracked by a reactive effect.
 * - Exposes `loadCandidate` for the confirm-before-load dialog.
 */
export const useProjectLoad = ({ activeSessionId }: UseProjectLoadInput) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [pendingLoadedProject, setPendingLoadedProject] = useState<PendingLoadedProject | null>(null);
    const [loadCandidate, setLoadCandidate] = useState<ProjectReference | null>(null);

    const loadProjectMutation = useLoadProject();

    const {
        pois,
        setArticles,
        setPois,
        setArticleInstanceSelections,
        handlePlaceArticleGroupsAsync,
        setActiveProject,
        setLastSavedSignature,
        setActiveDialog,
        setIsOpen
    } = usePlacementStore(
        useShallow((state) => ({
            pois: state.pois,
            setArticles: state.setArticles,
            setPois: state.setPois,
            setArticleInstanceSelections: state.setArticleInstanceSelections,
            handlePlaceArticleGroupsAsync: state.handlePlaceArticleGroupsAsync,
            setActiveProject: state.setActiveProject,
            setLastSavedSignature: state.setLastSavedSignature,
            setActiveDialog: state.setActiveProjectDialog,
            setIsOpen: state.setIsProjectPanelOpen
        }))
    );

    // Apply pending loaded project once the session and POIs are ready
    useEffect(() => {
        if (!pendingLoadedProject) {
            return;
        }

        if (activeSessionId !== pendingLoadedProject.sessionId) {
            return;
        }

        if (pendingLoadedProject.waitForPois && pendingLoadedProject.articleGroups.length > 0 && pois.length === 0) {
            return;
        }

        const applyLoadedProject = async () => {
            await handlePlaceArticleGroupsAsync(pendingLoadedProject.articleGroups);

            const state = usePlacementStore.getState();
            setActiveProject(pendingLoadedProject.project);
            setLastSavedSignature(createProjectSignature(state.articles, state.pois));
            setPendingLoadedProject(null);
            setLoadCandidate(null);
            setActiveDialog(ProjectsDialog.NONE);
            setIsOpen(false);
        };

        void applyLoadedProject();
    }, [
        activeSessionId,
        handlePlaceArticleGroupsAsync,
        pendingLoadedProject,
        pois.length,
        setActiveDialog,
        setActiveProject,
        setIsOpen,
        setLastSavedSignature
    ]);

    const handleLoadProject = useCallback(
        async (project: ProjectReference) => {
            try {
                const response = await loadProjectMutation.mutateAsync({ projectId: project.id });
                if (!response.success) {
                    toastNotifications.error({
                        title: t('error'),
                        message: response.errorMessage ?? t('errorLoadProject')
                    });
                    return;
                }

                const isSessionSwitch = response.sessionId !== activeSessionId;
                setArticles({});
                setArticleInstanceSelections({});
                if (isSessionSwitch) {
                    setPois([]);
                } else {
                    setPois(pois.map((poi) => ({ ...poi, articles: {} })));
                }

                const nextParams = new URLSearchParams(searchParams);
                nextParams.set('formingsession', response.sessionId);
                nextParams.set('projectId', project.id);

                setLastSavedSignature('');
                setPendingLoadedProject({
                    sessionId: response.sessionId,
                    project,
                    articleGroups: response.articleGroups,
                    waitForPois: isSessionSwitch
                });

                setSearchParams(nextParams, { replace: true });
            } catch (error) {
                toastNotifications.error({
                    title: t('error'),
                    message: error instanceof Error ? error.message : t('errorLoadProject')
                });
            }
        },
        [
            activeSessionId,
            loadProjectMutation,
            pois,
            searchParams,
            setArticleInstanceSelections,
            setArticles,
            setLastSavedSignature,
            setPois,
            setSearchParams,
            t
        ]
    );

    const openLoadDialog = useCallback(
        (candidate: ProjectReference) => {
            setLoadCandidate(candidate);
            setActiveDialog(ProjectsDialog.LOAD);
        },
        [setActiveDialog]
    );

    return {
        pendingLoadedProject,
        loadCandidate,
        setLoadCandidate,
        isLoadPending: loadProjectMutation.isPending,
        handleLoadProject,
        openLoadDialog
    };
};
